"use client";

import StoreBox from "../store/store-box";
import ProductDetails from "./product-details";
import NotFound from "../common/NotFound";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { getActiveCart } from "../slice/cart-slice";
import { useEffect, useMemo, useState } from "react";
import { CONFIG } from "../../lib/util/config";
import Skeleton from "react-loading-skeleton";
import { useToken } from "../../lib/context/auth-context";
import styles from "./index.module.css";
import SimilarProducts from "./similarProducts";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { setActiveCategory, setCategories } from "../slice/category-slice";
import { useHistoryManager } from "../history/useHistoryManager";

const ProductPage = (props) => {
  const cart = useAppSelector(getActiveCart);
  const {
    urlData,
    showCart,
    storePath,
    accessPointId,
    organisation,
    accessPoint,
    accessPoints,
    serverToken,
    navLocation,
  } = props;

  const { token } = useToken();

  /* =========================
     RESOLVE PRODUCT (🔥 SOURCE OF TRUTH)
     ========================= */

  const resolvedProduct = useMemo(() => {
    if (!urlData) return null;
    return urlData.product ?? urlData.product ?? null;
  }, [urlData]);

  const [productRes, setProductRes] = useState(urlData?.product ?? null);
  const [loading, setLoading] = useState(!resolvedProduct);

  const [isSwitching, setSwitching] = useState(false);
  const dispatch = useAppDispatch();

  const { navigateTo } = useHistoryManager();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 750);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* =========================
     SYNC STATE FROM URL DATA
     ========================= */

  useEffect(() => {
    if (!urlData) return;

    // Restore organisation context from snapshot
    if (urlData.organisation) {
      dispatch(setOrganisation(urlData.organisation));
    }

    if (urlData.accessPointId) {
      dispatch(setAccessPointId(urlData.accessPointId));
    }

    if (urlData.accessPoint) {
      dispatch(setAccessPoint(urlData.accessPoint));
    }

    if (Array.isArray(urlData.accessPoints)) {
      dispatch(setAccessPoints(urlData.accessPoints));
    }

    if (urlData.product) {
      setProductRes(urlData?.product);
    }
  }, [urlData]);

  useEffect(() => {
    if (resolvedProduct) {
      setProductRes(resolvedProduct);
      setLoading(false);
      setSwitching(false);
    } else {
      setProductRes(null);
      setLoading(true);
    }
  }, [resolvedProduct]);

  /* =========================
     FETCH FALLBACK (ONLY IF NEEDED)
     ========================= */

  const checkSum = useMemo(() => {
    if (typeof window === "undefined") return null;
    const slug = window.location.pathname.split("/").filter(Boolean);
    const productIndex = slug.indexOf("product");
    if (productIndex === -1 || slug.length <= productIndex + 1) return null;
    return slug[productIndex + 1].split("-").pop();
  }, []);

  useEffect(() => {
    if (resolvedProduct) return;

    if (!checkSum || !accessPointId || !organisation) return;
    let cancelled = false;

    async function fetchProduct() {
      setLoading(true);

      try {
        const res = await fetch(
          `${CONFIG.apiUrl}/product/public/organisation/${organisation.orgId}/checksum/${checkSum}/info?useFeed=true&accessPointId=${accessPointId}`,
          {
            headers: {
              Authorization: `Bearer ${token || serverToken}`,
              "Content-Type": "application/json",
            },
            next: { revalidate: 10 },
          },
        );

        const json = res.ok ? await res.json() : null;

        if (!cancelled) {
          setProductRes(json?.data?.product || null);
        }
      } catch {
        if (!cancelled) setProductRes(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [
    resolvedProduct,
    checkSum,
    accessPointId,
    organisation,
    token,
    serverToken,
  ]);

  /* =========================
     RENDER
     ========================= */

  return (
    <StoreBox {...props} storePath={storePath} showMarketDiv={true}>
      <div className={styles.productDetails}>
        {loading && !isSwitching && (
          <div
            style={{
              display: "flex",
              height: isMobile ? "80vh" : "40rem",
              width: "100%",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-evenly",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                width: isMobile ? "100%" : "40%",
                height: isMobile ? "100%" : "85%",
                flexDirection: "column",
                gap: "1rem",
                // marginBottom: isMobile ? "2rem" : 0,
                alignItems: isMobile ? "center" : "initial",
                padding: isMobile ? "0 2rem" : 0,
              }}
            >
              <div
                style={{
                  width: isMobile ? "80%" : "100%",
                  height: isMobile ? "100%" : "80%",
                }}
              >
                <Skeleton width="100%" height="100%" />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: isMobile ? "50px" : "60px",
                    height: isMobile ? "50px" : "60px",
                  }}
                >
                  <Skeleton width="100%" height="100%" />
                </div>
                <div
                  style={{
                    width: isMobile ? "50px" : "60px",
                    height: isMobile ? "50px" : "60px",
                  }}
                >
                  <Skeleton width="100%" height="100%" />
                </div>
                <div
                  style={{
                    width: isMobile ? "50px" : "60px",
                    height: isMobile ? "50px" : "60px",
                  }}
                >
                  <Skeleton width="100%" height="100%" />
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                height: isMobile ? "100%" : "85%",
                width: isMobile ? "80%" : "40%",
                flexDirection: "column",
                gap: "1rem",
                padding: isMobile ? "4rem 2rem" : "4rem",
                paddingTop: isMobile ? "4rem" : "1rem",
              }}
            >
              <div style={{ width: "100%", height: "42%" }}>
                <Skeleton
                  width="100%"
                  height="38%"
                  style={{ marginBottom: isMobile ? "1rem" : "2rem" }}
                />
                <Skeleton width="85%" height="38%" />
              </div>

              <div
                style={{
                  width: "100%",
                  // height: "40%",
                  flex: 1,
                  display: "flex",
                  gap: "1rem",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "48%",
                    display: "flex",
                    gap: "1rem",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "50%",
                      height: isMobile ? "100%" : "80%",
                    }}
                  >
                    <Skeleton height="100%" />
                  </div>
                  <div
                    style={{
                      width: "50%",
                      height: isMobile ? "100%" : "80%",
                    }}
                  >
                    <Skeleton height="100%" />
                  </div>
                </div>
                <div style={{ height: isMobile ? "30%" : "4rem" }}>
                  <Skeleton width="100%" height="100%" />
                </div>
              </div>
            </div>
          </div>
        )}
        {isSwitching && !loading && (
          <div
            style={{
              display: "flex",
              height: isMobile ? "80vh" : "40rem",
              width: "100%",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-evenly",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                width: isMobile ? "100%" : "40%",
                height: isMobile ? "100%" : "85%",
                flexDirection: "column",
                gap: "1rem",
                // marginBottom: isMobile ? "2rem" : 0,
                alignItems: isMobile ? "center" : "initial",
                padding: isMobile ? "0 2rem" : 0,
              }}
            >
              <div
                style={{
                  width: isMobile ? "80%" : "100%",
                  height: isMobile ? "100%" : "80%",
                }}
              >
                <Skeleton width="100%" height="100%" />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: isMobile ? "50px" : "60px",
                    height: isMobile ? "50px" : "60px",
                  }}
                >
                  <Skeleton width="100%" height="100%" />
                </div>
                <div
                  style={{
                    width: isMobile ? "50px" : "60px",
                    height: isMobile ? "50px" : "60px",
                  }}
                >
                  <Skeleton width="100%" height="100%" />
                </div>
                <div
                  style={{
                    width: isMobile ? "50px" : "60px",
                    height: isMobile ? "50px" : "60px",
                  }}
                >
                  <Skeleton width="100%" height="100%" />
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                height: isMobile ? "100%" : "85%",
                width: isMobile ? "80%" : "40%",
                flexDirection: "column",
                gap: "1rem",
                padding: isMobile ? "4rem 2rem" : "4rem",
                paddingTop: isMobile ? "4rem" : "1rem",
              }}
            >
              <div style={{ width: "100%", height: "42%" }}>
                <Skeleton
                  width="100%"
                  height="38%"
                  style={{ marginBottom: isMobile ? "1rem" : "2rem" }}
                />
                <Skeleton width="85%" height="38%" />
              </div>

              <div
                style={{
                  width: "100%",
                  // height: "40%",
                  flex: 1,
                  display: "flex",
                  gap: "1rem",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "48%",
                    display: "flex",
                    gap: "1rem",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "50%",
                      height: isMobile ? "100%" : "80%",
                    }}
                  >
                    <Skeleton height="100%" />
                  </div>
                  <div
                    style={{
                      width: "50%",
                      height: isMobile ? "100%" : "80%",
                    }}
                  >
                    <Skeleton height="100%" />
                  </div>
                </div>
                <div style={{ height: isMobile ? "30%" : "4rem" }}>
                  <Skeleton width="100%" height="100%" />
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !isSwitching && productRes && (
          <ProductDetails
            cart={cart}
            product={productRes}
            showCart={showCart}
            enablePreview
            accessPoint={accessPoint}
            organisation={organisation}
          />
        )}

        {!loading && !productRes && !isSwitching && <NotFound />}
      </div>

      <SimilarProducts
        product={productRes}
        setProduct={setProductRes}
        organisation={organisation}
        accessPoint={accessPoint}
        accessPoints={accessPoints}
        token={token || serverToken}
        storePath={storePath}
        isSwitching={isSwitching}
        setSwitching={setSwitching}
        navLocation={navLocation}
      />
    </StoreBox>
  );
};

export default ProductPage;
